import { formatDate, USER_ROLE_LABELS, whatsappStatusLabel } from '@crm/shared';
import { Badge, Card, CardContent } from '@crm/ui';
import { MapPin, UserRound } from 'lucide-react';
import { EmptyState } from '@/components';
import type { SubscriberDetail } from '../api';

function Campo({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="space-y-0.5">
      <p className="text-xs text-muted-foreground">{rotulo}</p>
      <p className="text-sm font-medium">{valor}</p>
    </div>
  );
}

export function SubscriberData({ detail }: { detail: SubscriberDetail }) {
  const { subscriber, units, users } = detail;

  return (
    <div className="space-y-4">
      <Card variant="flush">
        <CardContent className="grid gap-4 py-5 sm:grid-cols-2 lg:grid-cols-3">
          <Campo rotulo="Nome" valor={subscriber.name} />
          <Campo rotulo="Razao social" valor={subscriber.legalName ?? '-'} />
          <Campo rotulo="Documento" valor={subscriber.document ?? '-'} />
          <Campo rotulo="Telefone" valor={subscriber.phone ?? '-'} />
          <Campo rotulo="Ramo" valor={subscriber.businessType} />
          <Campo rotulo="Assinante desde" valor={formatDate(subscriber.createdAt)} />
        </CardContent>
      </Card>

      <Card variant="flush">
        <CardContent className="space-y-3 py-5">
          <p className="text-sm font-medium">Unidades</p>

          {units.length === 0 ? (
            <EmptyState icon={<MapPin />} title="Nenhuma unidade cadastrada" />
          ) : (
            <ul className="divide-y divide-border">
              {units.map((unit) => {
                const estado = whatsappStatusLabel(unit.whatsappStatus);

                return (
                  <li key={unit.id} className="flex flex-wrap items-center gap-2 py-3">
                    <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="font-medium">{unit.name}</span>
                    {unit.city ? (
                      <span className="text-sm text-muted-foreground">{unit.city}</span>
                    ) : null}
                    <span className="ml-auto flex items-center gap-2">
                      {unit.whatsappFrom ? (
                        <span className="text-sm text-muted-foreground">{unit.whatsappFrom}</span>
                      ) : null}
                      <Badge variant="outline">{estado.label}</Badge>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card variant="flush">
        <CardContent className="space-y-3 py-5">
          <p className="text-sm font-medium">Usuarios</p>

          {users.length === 0 ? (
            <EmptyState icon={<UserRound />} title="Nenhum usuario cadastrado" />
          ) : (
            <ul className="divide-y divide-border">
              {users.map((user) => (
                <li key={user.id} className="flex flex-wrap items-center gap-2 py-3">
                  <UserRound className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="font-medium">{user.name}</span>
                  <span className="text-sm text-muted-foreground">{user.email}</span>
                  <span className="ml-auto flex items-center gap-2">
                    <Badge variant="outline">
                      {USER_ROLE_LABELS[user.role as keyof typeof USER_ROLE_LABELS] ?? user.role}
                    </Badge>
                    {user.isActive ? null : (
                      <Badge variant="outline" className="border-destructive/50 text-destructive">
                        Inativo
                      </Badge>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
